import { useState, useMemo, useCallback, useEffect, Fragment } from 'react'
import { StatusBadge, Avatar, DealModal } from './UI'
import { extractUrls } from '../lib/dataHealth'
import { fmt, MONTHS_ORDER, ALL_STATUSES, DATE_ACTION_LABELS, getAgentCommission, isPaidStatus, getRelevantCommissions } from '../lib/constants'
import { deleteDeal, updateDeal } from '../lib/supabase'
import { canEditDeal, canExportDeals } from '../lib/permissions'
import { leadToDealPrefill } from '../lib/leadConversion'

// Export deals to Excel (.xlsx). SheetJS is code-split and only loads on click.
async function exportDealsToXlsx(deals, isAdminView) {
  if (!deals?.length) return
  const XLSX = await import('xlsx')

  const rows = deals.map(d => {
    const row = {
      Date: d.date || '',
      Creator: d.creator || '',
      Brand: d.brand || '',
      Client: d.client || '',
      Deliverables: d.deliverables || '',
      Amount: Number(d.deal_amount) || 0,
      Status: d.status || '',
      Agent: d.agent || '',
      Month: d.month || '',
      'Contact Person': d.contact_person || '',
      Email: d.email || '',
      Notes: d.notes || '',
      'Date Action': d.date_action || '',
    }
    if (isAdminView) {
      row['Commission Total'] = Number(d.commission_total) || 0
      row["Steve's Cut"] = Number(d.commission_steve) || 0
      row["Mirelle's Cut"] = Number(d.commission_mirelle) || 0
      row["Lydie's Cut"] = Number(d.commission_lydie) || 0
      row["Loizza's Cut"] = Number(d.commission_loizza) || 0
      row["Vincent's Cut"] = Number(d.commission_vincent) || 0
      row['Double Shots %'] = Number(d.double_shots_pct) || 0
      row['Double Shots Cut'] = (Number(d.deal_amount) || 0) * (Number(d.double_shots_pct) || 0) / 100
    }
    return row
  })

  const ws = XLSX.utils.json_to_sheet(rows)
  // Auto width per column
  const cols = Object.keys(rows[0] || {}).map(key => ({
    wch: Math.min(50, Math.max(key.length, ...rows.map(r => String(r[key] ?? '').length))) + 2
  }))
  ws['!cols'] = cols

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Deals')

  const today = new Date().toISOString().slice(0, 10)
  XLSX.writeFile(wb, `AgencyBase-Deals-${today}.xlsx`)
}

// =============================================================================
// DEALS.JS — Deal pipeline, table, calendar and kanban views
// =============================================================================
// SECTIONS:
//   [FILTERS]     Filter state management (lifted to App.js, accessed via props)
//   [YEAR]        Year filter with historical data detection (timezone-safe)
//   [FILTERED]    Memoized filtered + sorted deal list
//   [HANDLERS]    Status/date-action inline edit, delete, bulk ops
//   [TABLE]       Table view (sortable columns, expandable rows)
//   [CALENDAR]    Calendar view (monthly grid with deal markers)
//   [KANBAN]      Board view (deals grouped by status columns)
//   [TOOLBAR]     Top bar (search, filters, view toggle, stats, year dropdown)
// =============================================================================

// One chip per real http(s) link. Content may hold a TikTok and its Instagram
// repost, so each gets its own chip. Text saved in a link field (e.g. "No
// Contract") is shown as text, never as a link: as an href it would resolve to
// a page inside AgencyBase.
function LinkChips({ value, label, color, bg, multiple }) {
  const chip = { background: bg, color, padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }
  if (!value || !String(value).trim()) return <span style={{ color: 'var(--text3)', fontSize: 10, opacity: 0.5 }}>--</span>
  const urls = extractUrls(value)
  if (!urls.length) return <span title={String(value)} style={{ ...chip, background: 'var(--bg3)', color: 'var(--text3)', fontWeight: 500 }}>{label}: text</span>
  const list = multiple ? urls : urls.slice(0, 1)
  return list.map((u, i) => (
    <a key={u + i} href={u} target="_blank" rel="noreferrer" title={u} style={chip}>{list.length > 1 ? `${label} ${i + 1}` : label}</a>
  ))
}

export default function Deals({ deals, allDeals, currentUser, onRefresh, viewMode, activeAgentName: activeAgent, isMobile, privacyMode, filters, onFiltersChange, convertLead, onConvertLeadHandled, focusDealId, onFocusDealHandled, team, managedCreators, onToast }) {

  // --- [FILTERS] Persistent filter state — lifted to App.js so it survives tab switches ---
  const { search, filterCreator, filterAgent, filterStatus, filterMonth, filterBrand, filterClient,
          filterYear, sortField, sortDir, dealView, showFilters, hiddenCols } = filters
  const setFilter = useCallback((key, val) => onFiltersChange(prev => ({ ...prev, [key]: val })), [onFiltersChange])
  // Convenience setters (used throughout the component JSX)
  const setSearch = val => setFilter('search', val)
  const setFilterCreator = val => setFilter('filterCreator', val)
  const setFilterAgent = val => setFilter('filterAgent', val)
  const setFilterStatus = val => setFilter('filterStatus', val)
  const setFilterMonth = val => setFilter('filterMonth', val)
  const setFilterBrand = val => setFilter('filterBrand', val)
  const setFilterClient = val => setFilter('filterClient', val)
  const setFilterYear = val => setFilter('filterYear', val)
  const setSortField = val => setFilter('sortField', val)
  const setSortDir = val => setFilter('sortDir', val)
  const setDealView = val => setFilter('dealView', val)
  const setShowFilters = val => setFilter('showFilters', val)
  const setHiddenCols = val => setFilter('hiddenCols', typeof val === 'function' ? val(hiddenCols) : val)

  // Local-only UI state (OK to reset on remount)
  // Track modal by deal ID so it survives data reloads (deals array getting a new reference)
  const [modalId, setModalId] = useState(null)
  const modal = modalId === 'new' ? 'new' : deals.find(d => d.id === modalId) || null
  const setModal = (deal) => setModalId(deal === 'new' ? 'new' : deal?.id || null)
  const [expandedRow, setExpandedRow] = useState(null)
  const [editingStatus, setEditingStatus] = useState(null)
  const [editingDateAction, setEditingDateAction] = useState(null)
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1) })
  const [showColPicker, setShowColPicker] = useState(false)
  const [searchFocused, setSearchFocused] = useState(false)
  // Bulk-select for batch status updates (admin/sales)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [bulkUpdating, setBulkUpdating] = useState(false)
  // Pagination — defaults to 50 / page. 'all' shows everything.
  const [pageSize, setPageSize] = useState(50)
  const [currentPage, setCurrentPage] = useState(1)

  // Auto-open DealModal pre-filled when a CRM lead is converted.
  //
  // Do NOT clear convertLead here. Opening the modal and clearing the lead in
  // the same effect batches into one render, so by the time DealModal mounts
  // the lead is already gone and `prefill` evaluates to null — the form opens
  // completely blank and every field the agent entered in the CRM is lost.
  // The lead is released when the modal closes instead.
  useEffect(() => {
    if (convertLead) setModalId('new')
  }, [convertLead])

  const releaseConvertLead = useCallback(() => {
    if (onConvertLeadHandled) onConvertLeadHandled()
  }, [onConvertLeadHandled])

  // Open a specific deal picked in global search. Safe to release the prop in
  // the same effect, unlike convertLead above: the modal is resolved from
  // modalId against the deals list, not from this prop, so nothing downstream
  // reads it after the id is set.
  useEffect(() => {
    if (!focusDealId) return
    setModalId(focusDealId)
    if (onFocusDealHandled) onFocusDealHandled()
  }, [focusDealId, onFocusDealHandled])

  const isAdminView = viewMode === 'admin'
  const isAdmin = currentUser?.role === 'admin'
  const isSales = currentUser?.role === 'sales'
  // (Role checks above are kept inline because they're used in many places in
  // this file. New code should prefer lib/permissions.js helpers — imported
  // below for the deal-level checks.)
  const hide = privacyMode
  const redact = (val) => hide ? '•••••' : val
  const agentName = activeAgent || currentUser?.agent_name

  const creators = useMemo(() => ['All', ...new Set(deals.map(d => d.creator).filter(Boolean))].sort(), [deals])
  const agents = useMemo(() => ['All', ...new Set(deals.map(d => d.agent).filter(Boolean))].sort(), [deals])
  const brands = useMemo(() => ['All', ...new Set(deals.map(d => d.brand).filter(Boolean))].sort(), [deals])
  const clients = useMemo(() => ['All', ...new Set(deals.map(d => d.client).filter(Boolean))].sort(), [deals])

  // --- [YEAR] Year filter — uses string slicing to avoid JS timezone bugs ---
  // (new Date('2026-01-01').getFullYear() returns 2025 in EST due to UTC parse)
  const currentYear = String(new Date().getFullYear())
  const years = useMemo(() => [...new Set((allDeals || deals).map(d => d.date ? String(d.date).slice(0, 4) : null).filter(Boolean))].sort().reverse(), [allDeals, deals])
  // Track which years contain only imported historical data
  const historicalYears = useMemo(() => {
    const yrs = new Set()
    ;(allDeals || deals).forEach(d => {
      if (d.is_historical && d.date) yrs.add(String(d.date).slice(0, 4))
    })
    return yrs
  }, [allDeals, deals])

  // --- [FILTERED] Memoized filtered + sorted deal list ---
  const filtered = useMemo(() => {
    // When viewing a non-current year, pull from allDeals (which includes historical)
    let d = filterYear && filterYear !== currentYear ? [...(allDeals || deals)] : [...deals]
    if (filterYear && filterYear !== 'All') d = d.filter(x => x.date && String(x.date).slice(0, 4) === filterYear)
    if (search) { const q = search.toLowerCase(); d = d.filter(x => [x.creator, x.brand, x.client, x.contact_person, x.status, x.deliverables].some(f => f && f.toLowerCase().includes(q))) }
    if (filterCreator !== 'All') d = d.filter(x => x.creator === filterCreator)
    if (filterAgent !== 'All') d = d.filter(x => x.agent === filterAgent)
    if (filterStatus !== 'All') d = d.filter(x => x.status === filterStatus)
    if (filterMonth !== 'All') d = d.filter(x => x.month === filterMonth)
    if (filterBrand !== 'All') d = d.filter(x => x.brand === filterBrand)
    if (filterClient !== 'All') d = d.filter(x => x.client === filterClient)
    // Pipeline ordering for status sort — follows the natural deal lifecycle
    const STATUS_ORDER = ['Concept', 'In Production', 'Waiting For Approval', 'Approved', 'Posted', 'Invoice Sent', 'Partially Paid', 'Paid/Done']
    const statusRank = s => {
      const i = STATUS_ORDER.indexOf(s)
      return i === -1 ? STATUS_ORDER.length : i
    }
    d.sort((a, b) => {
      if (sortField === 'status') {
        const ra = statusRank(a.status), rb = statusRank(b.status)
        return sortDir === 'asc' ? ra - rb : rb - ra
      }
      if (sortField === 'month') {
        const ma = MONTHS_ORDER.indexOf(a.month), mb = MONTHS_ORDER.indexOf(b.month)
        const ra = ma === -1 ? 99 : ma, rb = mb === -1 ? 99 : mb
        return sortDir === 'asc' ? ra - rb : rb - ra
      }
      let va = a[sortField] ?? '', vb = b[sortField] ?? ''
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va))
    })
    return d
  }, [deals, allDeals, search, filterCreator, filterAgent, filterStatus, filterMonth, filterBrand, filterClient, filterYear, currentYear, sortField, sortDir])

  const totalValue = filtered.reduce((s, d) => s + (d.deal_amount || 0), 0)

  // Pagination — derived view of `filtered`. KPIs/footer use full `filtered`.
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const paginated = useMemo(() => {
    if (pageSize === 'all') return filtered
    const start = (safePage - 1) * pageSize
    return filtered.slice(start, start + pageSize)
  }, [filtered, pageSize, safePage])
  // Reset to page 1 whenever the filter set changes (avoids landing on an empty page).
  useEffect(() => { setCurrentPage(1) }, [search, filterCreator, filterAgent, filterStatus, filterMonth, filterBrand, filterClient, filterYear, sortField, sortDir, pageSize])
  const myTotalComm = filtered.reduce((s, d) => s + getAgentCommission(d, agentName), 0)
  const canEdit = deal => canEditDeal(currentUser, deal, agentName, managedCreators)

  // Both handlers below must inspect updateDeal's result, and a thrown error is
  // not the only failure mode. An RLS policy on UPDATE *filters rows* rather
  // than raising: a denied write comes back as `error: null` with an empty
  // `data`, which is indistinguishable from success unless the row count is
  // checked. That is the silent case — the edit appears to take, then reverts
  // on the next refresh with nothing explaining why.
  const updateFailure = (result) => {
    if (!result) return 'No response from the server.'
    if (result.error) return result.error.message
    if (Array.isArray(result.data) && result.data.length === 0) {
      return "The change was rejected — you may not have permission to edit this deal."
    }
    return null
  }

  const handleStatusChange = async (deal, newStatus) => {
    setEditingStatus(null)
    const problem = updateFailure(await updateDeal(deal.id, { status: newStatus }))
    if (problem) {
      onToast?.(`Couldn't update ${deal.creator} × ${deal.brand}: ${problem}`, 'error')
    }
    // Refresh either way: on success to show the new status, on failure to put
    // the real stored value back rather than leaving a stale optimistic one.
    onRefresh()
  }

  const handleBulkStatusChange = async (newStatus) => {
    if (!selectedIds.size || !newStatus) return
    setBulkUpdating(true)
    const ids = Array.from(selectedIds)
    // Sequential to keep activity log clean; ~30 deals = 1–2s round-trip
    const failed = []
    for (const id of ids) {
      const problem = updateFailure(await updateDeal(id, { status: newStatus }))
      if (problem) failed.push({ id, message: problem })
    }
    setBulkUpdating(false)

    // Keep the failures selected so the user can see exactly what still needs
    // attention and retry it. Clearing the whole selection here would hide the
    // failures completely and report a partial write as a clean one.
    setSelectedIds(new Set(failed.map(f => f.id)))

    const ok = ids.length - failed.length
    if (!failed.length) {
      onToast?.(`Updated ${ok} deal${ok === 1 ? '' : 's'} to ${newStatus}.`)
    } else if (!ok) {
      onToast?.(`None of the ${ids.length} deals could be updated. ${failed[0].message}`, 'error')
    } else {
      onToast?.(`Updated ${ok} of ${ids.length}. ${failed.length} still selected and need attention.`, 'error')
    }
    onRefresh()
  }

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  const selectAllVisible = (visibleIds) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      const allSelected = visibleIds.every(id => next.has(id))
      if (allSelected) visibleIds.forEach(id => next.delete(id))
      else visibleIds.forEach(id => next.add(id))
      return next
    })
  }
  const handleDateActionChange = async (deal, newDate) => {
    setEditingDateAction(null)
    await updateDeal(deal.id, { date_action: newDate || null })
    onRefresh()
  }
  const toggleSort = field => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else { setSortField(field); setSortDir(field === 'status' ? 'asc' : 'desc') }
  }
  const handleDelete = async deal => { if (!window.confirm(`Delete: ${deal.creator} x ${deal.brand}?`)) return; await deleteDeal(deal.id); onRefresh() }
  const sort = f => sortField === f ? (sortDir === 'asc' ? ' ^' : ' v') : ''

  const sel = { padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 12, outline: 'none', cursor: 'pointer' }
  const th = field => ({ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: sortField === field ? 'var(--accent)' : 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', borderBottom: '1px solid var(--border)', background: 'var(--bg3)' })
  const td = { padding: '11px 14px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle', fontSize: 13 }

  const clearFilters = () => { setSearch(''); setFilterCreator('All'); setFilterAgent('All'); setFilterStatus('All'); setFilterMonth('All'); setFilterBrand('All'); setFilterClient('All'); setFilterYear(currentYear) }
  const hasFilters = search || filterCreator !== 'All' || filterAgent !== 'All' || filterStatus !== 'All' || filterMonth !== 'All' || filterBrand !== 'All' || filterClient !== 'All' || filterYear !== currentYear

  return (
    <>
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 20, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 0 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 22 : 26, fontWeight: 700, letterSpacing: '-0.5px' }}>
            {isAdminView ? 'All Deals' : 'My Deals'}
            <span style={{ fontSize: 15, color: 'var(--text3)', fontFamily: 'var(--font-body)', fontWeight: 400, marginLeft: 8 }}>({filtered.length})</span>
            {hide && <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 600, color: '#8b5cf6', background: 'rgba(139,92,246,0.12)', padding: '3px 8px', borderRadius: 6 }}>Privacy</span>}
          </h1>
          {!isAdminView && <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>Your assigned deals only</p>}
        </div>
        {!hide && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {canExportDeals(currentUser) && (
            <button
              onClick={() => exportDealsToXlsx(filtered, isAdminView)}
              disabled={!filtered.length}
              style={{
                padding: '9px 16px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)',
                fontWeight: 600, fontSize: 13,
                cursor: filtered.length ? 'pointer' : 'not-allowed',
                opacity: filtered.length ? 1 : 0.5,
                fontFamily: 'var(--font-body)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
              title="Export filtered deals to Excel"
            >
              ⬇ Export Excel
            </button>
            )}
            <button onClick={() => setModal('new')} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #f97316, #ef4444)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>+ New Deal</button>
          </div>
        )}
      </div>

      {/* Filters */}
      {(() => {
        const activeFilterCount = [filterCreator, filterAgent, filterStatus, filterMonth, filterBrand, filterClient].filter(f => f !== 'All').length + (filterYear && filterYear !== currentYear ? 1 : 0)
        return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: isMobile ? '100%' : 220 }}>
              <input placeholder="Search deals..." value={search} onChange={e => setSearch(e.target.value)} onFocus={() => setSearchFocused(true)} onBlur={() => setTimeout(() => setSearchFocused(false), 200)} style={{ ...sel, width: '100%', padding: '7px 12px' }} />
              {search && searchFocused && filtered.length > 0 && filtered.length <= 10 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.4)', zIndex: 200, maxHeight: 260, overflowY: 'auto' }}>
                  {filtered.map((deal, i) => (
                    <div key={deal.id || i} onMouseDown={() => {
                      setSearchFocused(false)
                      setDealView('table')
                      setExpandedRow(i)
                      setTimeout(() => { const el = document.getElementById(`deal-row-${deal.id}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }) }, 50)
                    }} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.1s' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(249,115,22,0.06)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{deal.creator} × {deal.brand}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{deal.client || ''}{deal.deal_amount ? ` · ${fmt(deal.deal_amount)}` : ''} · {deal.status}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowFilters(f => !f)} style={{
              ...sel, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              background: showFilters || activeFilterCount > 0 ? 'rgba(249,115,22,0.08)' : 'var(--bg2)',
              borderColor: activeFilterCount > 0 ? 'var(--accent)' : 'var(--border)',
              color: activeFilterCount > 0 ? 'var(--accent)' : 'var(--text2)'
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
              Filters{activeFilterCount > 0 && <span style={{ background: 'var(--accent)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>{activeFilterCount}</span>}
            </button>
            {hasFilters && <button onClick={clearFilters} style={{ ...sel, color: '#f87171', borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Clear all</button>}
            <div style={{ display: 'flex', gap: 4, background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)', padding: 3, marginLeft: 'auto' }}>
              {[{ id: 'table', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> },
                { id: 'calendar', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> }
              ].map(v => (
                <button key={v.id} onClick={() => setDealView(v.id)} title={v.id === 'table' ? 'Table view' : 'Calendar view'} style={{
                  padding: '5px 10px', borderRadius: 6, border: 'none',
                  background: dealView === v.id ? 'var(--accent)' : 'transparent',
                  color: dealView === v.id ? '#fff' : 'var(--text3)',
                  fontWeight: 600, fontSize: 11, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>{v.icon}</button>
              ))}
            </div>
            {dealView === 'table' && !isMobile && (
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowColPicker(p => !p)} style={{ ...sel, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18M3 12h18"/></svg>
                  Columns
                </button>
                {showColPicker && (
                  <div className="pop-in" style={{ position: 'absolute', top: '110%', right: 0, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 8, minWidth: 180, zIndex: 100, boxShadow: 'var(--shadow-lg)' }}>
                    {[['contact','Contact'],['links','Links'],['commission','Commission'],['agent','Agent'],['month','Month'],['deliverables','Deliverables']].map(([k, label]) => (
                      <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: 'var(--text2)' }}>
                        <input type="checkbox" checked={!hiddenCols.has(k)} onChange={() => setHiddenCols(prev => { const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k); return next })} />
                        {label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {showFilters && (
            <div className="pop-in" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '12px', background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <select value={filterYear || currentYear} onChange={e => setFilterYear(e.target.value)} style={{ ...sel, ...(filterYear && filterYear !== currentYear ? { borderColor: 'var(--accent)', color: 'var(--accent)', fontWeight: 600 } : {}) }}>
                {years.map(y => <option key={y} value={y}>{y}{y === currentYear ? ' (Current)' : historicalYears.has(y) ? ' (Imported)' : ''}</option>)}
                <option value="All">All Years</option>
              </select>
              <select value={filterCreator} onChange={e => setFilterCreator(e.target.value)} style={sel}>
                {creators.map(c => <option key={c} value={c}>{c === 'All' ? 'All Creators' : c}</option>)}
              </select>
              {isAdminView && <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)} style={sel}>
                {agents.map(a => <option key={a} value={a}>{a === 'All' ? 'All Agents' : a}</option>)}
              </select>}
              <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)} style={sel}>
                {brands.map(b => <option key={b} value={b}>{b === 'All' ? 'All Brands' : b}</option>)}
              </select>
              <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={sel}>
                {clients.map(c => <option key={c} value={c}>{c === 'All' ? 'All Clients' : c}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={sel}>
                <option value="All">All Statuses</option>
                {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={sel}>
                <option value="All">All Months</option>
                {MONTHS_ORDER.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}
        </div>
        )
      })()}

      {/* Calendar View */}
      {dealView === 'calendar' && (() => {
        const year = calMonth.getFullYear()
        const month = calMonth.getMonth()
        const firstDay = new Date(year, month, 1).getDay()
        const daysInMonth = new Date(year, month + 1, 0).getDate()
        const today = new Date()
        const todayStr = today.toISOString().slice(0, 10)
        const monthName = calMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

        // Group filtered deals by date_action
        const dealsByDate = {}
        filtered.forEach(d => {
          if (!d.date_action) return
          const key = d.date_action
          if (!dealsByDate[key]) dealsByDate[key] = []
          dealsByDate[key].push(d)
        })

        // Count deals without dates
        const noDateCount = filtered.filter(d => DATE_ACTION_LABELS[d.status] && !d.date_action).length

        const cells = []
        // Empty cells before first day
        for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} style={{ minHeight: isMobile ? 60 : 90, background: 'var(--bg)', borderRadius: 6, opacity: 0.3 }} />)
        // Day cells
        for (let day = 1; day <= daysInMonth; day++) {
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
          const dayDeals = dealsByDate[dateStr] || []
          const isToday = dateStr === todayStr
          const isPast = new Date(dateStr) < new Date(todayStr)
          cells.push(
            <div key={day} style={{
              minHeight: isMobile ? 60 : 90, background: isToday ? 'rgba(249,115,22,0.06)' : 'var(--bg2)',
              border: isToday ? '1.5px solid var(--accent)' : '1px solid var(--border)',
              borderRadius: 8, padding: '4px 6px', overflow: 'hidden',
              position: 'relative'
            }}>
              <div style={{
                fontSize: 11, fontWeight: isToday ? 700 : 500,
                color: isToday ? 'var(--accent)' : isPast ? 'var(--text3)' : 'var(--text2)',
                marginBottom: 4
              }}>{day}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dayDeals.slice(0, isMobile ? 2 : 3).map((deal, i) => {
                  const label = DATE_ACTION_LABELS[deal.status]
                  const statusColor = deal.status === 'Concept' || deal.status === 'In Production' ? '#8b5cf6'
                    : deal.status === 'Approved' ? '#06b6d4'
                    : deal.status === 'Invoice Sent' ? '#f97316' : '#6b7280'
                  return (
                    <div key={deal.id || i} title={`${deal.brand} — ${deal.creator} (${label})`} style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 5px', borderRadius: 4,
                      background: `${statusColor}18`, color: statusColor, border: `1px solid ${statusColor}30`,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      cursor: 'pointer', lineHeight: 1.3
                    }} onClick={() => { setDealView('table'); setSearch(deal.brand) }}>
                      {deal.brand}
                      {!isMobile && <span style={{ opacity: 0.7 }}> · {deal.creator}</span>}
                    </div>
                  )
                })}
                {dayDeals.length > (isMobile ? 2 : 3) && (
                  <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 600, padding: '1px 4px' }}>
                    +{dayDeals.length - (isMobile ? 2 : 3)} more
                  </div>
                )}
              </div>
            </div>
          )
        }

        return (
          <div style={{ marginBottom: 20 }}>
            {/* Calendar header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12,
              padding: '10px 16px', background: 'var(--bg2)', borderRadius: 'var(--radius)', border: '1px solid var(--border)'
            }}>
              <button onClick={() => setCalMonth(new Date(year, month - 1, 1))} style={{
                background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                padding: '5px 12px', color: 'var(--text2)', cursor: 'pointer', fontSize: 14, fontWeight: 600
              }}>←</button>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '-0.3px' }}>{monthName}</div>
                {noDateCount > 0 && <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, marginTop: 2 }}>{noDateCount} deal{noDateCount !== 1 ? 's' : ''} without dates</div>}
              </div>
              <button onClick={() => setCalMonth(new Date(year, month + 1, 1))} style={{
                background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                padding: '5px 12px', color: 'var(--text2)', cursor: 'pointer', fontSize: 14, fontWeight: 600
              }}>→</button>
            </div>

            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', padding: '4px 0', letterSpacing: '0.5px' }}>{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
              {cells}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
              {[
                { label: 'Due Date', color: '#8b5cf6', desc: 'Concept / In Production' },
                { label: 'Post Date', color: '#06b6d4', desc: 'Approved' },
                { label: 'Sent On', color: '#f97316', desc: 'Invoice Sent' },
              ].map(l => (
                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text3)' }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: `${l.color}30`, border: `1px solid ${l.color}50` }} />
                  <span style={{ fontWeight: 600, color: l.color }}>{l.label}</span>
                  <span>({l.desc})</span>
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {/* Mobile Card View */}
      {dealView === 'table' && isMobile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.length === 0 && <div style={{ padding: 48, textAlign: 'center', color: 'var(--text3)' }}>No deals match your filters.</div>}
          {paginated.map((deal, i) => (
            <div key={deal.id || i} onClick={() => !hide && setModal(deal)} style={{
              background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px',
              cursor: hide ? 'default' : 'pointer', transition: 'all 0.15s'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                  {currentUser?.role !== 'creator' && <Avatar name={deal.creator} size={32} />}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.brand}</div>
                    {currentUser?.role !== 'creator' && <div style={{ fontSize: 12, color: 'var(--text2)' }}>{deal.creator}{deal.agent ? ` \u00B7 via ${deal.agent}` : ''}</div>}
                  </div>
                </div>
                {!hide && <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-display)', flexShrink: 0, marginLeft: 8 }}>{deal.deal_amount ? fmt(deal.deal_amount) : '--'}</div>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <StatusBadge status={deal.status} />
                {deal.date_action && (() => {
                  const lbl = DATE_ACTION_LABELS[deal.status]
                  return lbl ? <span style={{ fontSize: 11, color: 'var(--text3)' }}>{lbl}: {deal.date_action?.slice(5)}</span> : null
                })()}
                {deal.client && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{deal.client}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table (desktop) */}
      {dealView === 'table' && !isMobile && (
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {(isAdmin || isSales) && (
                  <th style={{ ...th('_'), cursor: 'default', width: 36, paddingLeft: 12, paddingRight: 4 }} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      title="Select all on this page"
                      checked={paginated.length > 0 && paginated.every(d => selectedIds.has(d.id))}
                      ref={el => { if (el) el.indeterminate = paginated.some(d => selectedIds.has(d.id)) && !paginated.every(d => selectedIds.has(d.id)) }}
                      onChange={() => selectAllVisible(paginated.map(d => d.id))}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                <th style={th('date')} onClick={() => toggleSort('date')}>Date{sort('date')}</th>
                {currentUser?.role !== 'creator' && <th style={th('creator')} onClick={() => toggleSort('creator')}>Creator{sort('creator')}</th>}
                <th style={th('client')} onClick={() => toggleSort('client')}>Client{sort('client')}</th>
                <th style={th('brand')} onClick={() => toggleSort('brand')}>Brand{sort('brand')}</th>
                {!hiddenCols.has('deliverables') && <th style={th('deliverables')}>Deliverables</th>}
                {!hide && <th style={th('deal_amount')} onClick={() => toggleSort('deal_amount')}>Amount{sort('deal_amount')}</th>}
                {!hide && !hiddenCols.has('commission') && currentUser?.role !== 'creator' && <th style={th('commission')}>{isAdminView ? 'Commission' : 'My Commission'}</th>}
                {currentUser?.role !== 'creator' && !hide && !hiddenCols.has('agent') && <th style={th('agent')} onClick={() => toggleSort('agent')}>Agent{sort('agent')}</th>}
                {!hide && <th style={th('status')} onClick={() => toggleSort('status')}>Status{sort('status')}</th>}
                <th style={th('date_action')} onClick={() => toggleSort('date_action')}>Due/Post{sort('date_action')}</th>
                {!hiddenCols.has('month') && <th style={th('month')} onClick={() => toggleSort('month')}>Month{sort('month')}</th>}
                {!hide && !hiddenCols.has('contact') && currentUser?.role !== 'creator' && <th style={{ ...th('contact'), cursor: 'default' }}>Contact</th>}
                {!hide && !hiddenCols.has('links') && <th style={{ ...th('links'), cursor: 'default' }}>Links</th>}
                {!hide && <th style={{ ...th('_'), cursor: 'default' }}></th>}
              </tr>
            </thead>
            <tbody>
              {paginated.map((deal, i) => (
                <Fragment key={deal.id || i}>
                  <tr id={`deal-row-${deal.id}`} style={{ cursor: 'pointer', transition: 'background-color 0.15s', background: selectedIds.has(deal.id) ? 'rgba(59,130,246,0.10)' : expandedRow === i ? 'rgba(249,115,22,0.06)' : '' }}
                    onClick={() => !hide && setExpandedRow(expandedRow === i ? null : i)}
                    onMouseEnter={e => { if (!selectedIds.has(deal.id)) e.currentTarget.style.background = 'rgba(249,115,22,0.04)' }}
                    onMouseLeave={e => { if (!selectedIds.has(deal.id)) e.currentTarget.style.background = '' }}>
                    {(isAdmin || isSales) && (
                      <td style={{ ...td, paddingLeft: 12, paddingRight: 4, width: 36 }} onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(deal.id)}
                          onChange={() => toggleSelect(deal.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                    )}
                    <td style={{ ...td, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{deal.date?.slice(5) || '--'}</td>
                    {currentUser?.role !== 'creator' && <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar name={deal.creator} />
                        <span style={{ fontWeight: 600 }}>{deal.creator}</span>
                      </div>
                    </td>}
                    <td style={{ ...td, color: 'var(--text2)' }}>{deal.client || '--'}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{deal.brand}</div>
                    </td>
                    {!hiddenCols.has('deliverables') && <td style={{ ...td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text2)' }}>{deal.deliverables}</td>}
                    {!hide && <td style={{ ...td, fontWeight: 700, fontFamily: 'var(--font-display)', whiteSpace: 'nowrap' }}>{hide ? '•••••' : (deal.deal_amount ? fmt(deal.deal_amount) : '--')}</td>}
                    {!hide && !hiddenCols.has('commission') && currentUser?.role !== 'creator' && <td style={{ ...td, fontWeight: 600, color: isAdminView ? 'var(--text)' : '#10b981', whiteSpace: 'nowrap' }}>
                      {hide ? '•••••' : (isAdminView ? (
                        deal.commission_total ? (
                          <div>
                            <div>{fmt(deal.commission_total)}</div>
                            {(() => {
                              const ag = (deal.agent || '').toLowerCase()
                              if (['mirelle','lydie','loizza','katrina'].includes(ag) && deal.commission_steve) {
                                return <div style={{ fontSize: 11, fontWeight: 500, color: '#3b82f6', marginTop: 2 }}>Steve: {fmt(deal.commission_steve)}</div>
                              }
                              return null
                            })()}
                          </div>
                        ) : '--'
                      ) : (getAgentCommission(deal, agentName) ? fmt(getAgentCommission(deal, agentName)) : '--'))}
                    </td>}
                    {currentUser?.role !== 'creator' && !hide && !hiddenCols.has('agent') && <td style={{ ...td, color: 'var(--text2)' }}>{deal.agent || '--'}</td>}
                    {!hide && (
                      <td style={td} onClick={e => { e.stopPropagation(); canEdit(deal) && setEditingStatus(editingStatus === deal.id ? null : deal.id) }}>
                        {editingStatus === deal.id ? (
                          <select
                            autoFocus
                            value={deal.status}
                            onChange={e => { e.stopPropagation(); handleStatusChange(deal, e.target.value) }}
                            onBlur={() => setEditingStatus(null)}
                            onClick={e => e.stopPropagation()}
                            style={{ padding: '4px 8px', background: 'var(--bg2)', border: '1px solid var(--accent)', borderRadius: 6, color: 'var(--text)', fontSize: 12, cursor: 'pointer', outline: 'none', zIndex: 200, position: 'relative' }}
                          >
                            {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: canEdit(deal) ? 'pointer' : 'default' }}>
                            <StatusBadge status={deal.status} />
                            {canEdit(deal) && <span style={{ fontSize: 9, color: 'var(--text3)' }}>v</span>}
                          </div>
                        )}
                      </td>
                    )}
                    <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={e => {
                      e.stopPropagation()
                      const label = DATE_ACTION_LABELS[deal.status]
                      if (label && canEdit(deal)) setEditingDateAction(editingDateAction === deal.id ? null : deal.id)
                    }}>
                      {(() => {
                        const label = DATE_ACTION_LABELS[deal.status]
                        if (!label) return <span style={{ color: 'var(--text3)', fontSize: 11 }}>--</span>
                        if (editingDateAction === deal.id) return (
                          <input
                            type="date"
                            autoFocus
                            ref={el => { if (el) try { el.showPicker() } catch(_){} }}
                            defaultValue={deal.date_action || ''}
                            onChange={e => { if (e.target.value) handleDateActionChange(deal, e.target.value) }}
                            onBlur={() => setEditingDateAction(null)}
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => { if (e.key === 'Escape') setEditingDateAction(null) }}
                            style={{ padding: '4px 8px', background: 'var(--bg)', border: '1.5px solid var(--accent)', borderRadius: 8, color: 'var(--text)', fontSize: 12, outline: 'none', width: 140, cursor: 'pointer', colorScheme: 'dark' }}
                          />
                        )
                        if (!deal.date_action) return (
                          <span style={{ color: '#f59e0b', fontSize: 11, fontWeight: 600, cursor: canEdit(deal) ? 'pointer' : 'default' }}>
                            Not set{canEdit(deal) && <span style={{ fontSize: 9, color: 'var(--text3)', marginLeft: 3 }}>+</span>}
                          </span>
                        )
                        return (
                          <div style={{ cursor: canEdit(deal) ? 'pointer' : 'default' }}>
                            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{deal.date_action?.slice(5)}</div>
                          </div>
                        )
                      })()}
                    </td>
                    {!hiddenCols.has('month') && <td style={{ ...td, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{deal.month || '--'}</td>}
                    {!hide && !hiddenCols.has('contact') && currentUser?.role !== 'creator' && <td style={td}>
                      {hide ? '•••••' : (deal.contact_person ? (
                        <div>
                          <div style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{deal.contact_person}</div>
                          {deal.email && <a href={`mailto:${deal.email}`} style={{ fontSize: 11, color: '#3b82f6' }} onClick={e => e.stopPropagation()}>{deal.email}</a>}
                        </div>
                      ) : '--')}
                    </td>}
                    {!hide && !hiddenCols.has('links') && <td style={td} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <LinkChips value={deal.contract_url} label="Contract" color="#3b82f6" bg="rgba(59,130,246,0.12)" />
                        {currentUser?.role !== 'creator' && <LinkChips value={deal.invoice_url} label="Invoice" color="#10b981" bg="rgba(16,185,129,0.12)" />}
                        <LinkChips value={deal.content_url} label="Content" color="#a855f7" bg="rgba(168,85,247,0.12)" multiple />
                      </div>
                    </td>}
                    {!hide && <td style={td} onClick={e => e.stopPropagation()}>
                      {canEdit(deal) && (
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button onClick={() => setModal(deal)} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>Edit</button>
                          <button onClick={() => handleDelete(deal)} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, padding: '4px 8px', fontSize: 12, color: '#f87171', cursor: 'pointer' }}>Del</button>
                        </div>
                      )}
                    </td>}
                  </tr>
                  {!hide && expandedRow === i && (
                    <tr key={`exp-${i}`}>
                      <td colSpan={99} style={{ padding: '14px 20px', background: 'rgba(249,115,22,0.04)', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 16, fontSize: 12 }}>
                          {isAdminView && getRelevantCommissions(deal).map(({ label, value, color: c }) => (
                            <div key={label}><div style={{ color: 'var(--text3)', marginBottom: 3 }}>{label}</div><div style={{ fontWeight: 700, color: c }}>${(value || 0).toFixed(2)}</div></div>
                          ))}
                          {isAdminView && isAdmin && (Number(deal.double_shots_pct) || 0) > 0 && (
                            <div><div style={{ color: 'var(--text3)', marginBottom: 3 }}>Double Shots ({Number(deal.double_shots_pct)}%)</div><div style={{ fontWeight: 700, color: 'var(--accent)' }}>${((deal.deal_amount || 0) * Number(deal.double_shots_pct) / 100).toFixed(2)}</div></div>
                          )}
                          {deal.notes ? <div style={{ gridColumn: isAdminView ? 'span 2' : '1/-1' }}><div style={{ color: 'var(--text3)', marginBottom: 3 }}>Notes</div><div style={{ color: 'var(--text2)', lineHeight: 1.5 }}>{deal.notes}</div></div>
                          : <div style={{ color: 'var(--text3)', fontStyle: 'italic' }}>No notes.</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div style={{ padding: 48, textAlign: 'center', color: 'var(--text3)' }}>No deals match your filters.</div>}
      </div>
      )}

      {!hide && (
        <div style={{ marginTop: 10, display: 'flex', gap: isMobile ? 10 : 20, fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap' }}>
          <span>{filtered.length} deals</span>
          <span>Total: <strong style={{ color: 'var(--text)' }}>{hide ? '•••••' : fmt(totalValue)}</strong></span>
          {currentUser?.role !== 'creator' && <span>Paid: <strong style={{ color: '#10b981' }}>{hide ? '•••••' : fmt(filtered.filter(d => isPaidStatus(d.status)).reduce((s, d) => s + (d.deal_amount || 0), 0))}</strong></span>}
          {isAdminView && isAdmin && <span>Total commission: <strong style={{ color: '#8b5cf6' }}>{hide ? '•••••' : fmt(filtered.reduce((s, d) => s + (d.commission_total || 0), 0))}</strong></span>}
          {isAdminView && isAdmin && (() => {
            const dsTotal = filtered.reduce((s, d) => s + (d.deal_amount || 0) * (Number(d.double_shots_pct) || 0) / 100, 0)
            return dsTotal > 0 ? <span>Double Shots: <strong style={{ color: 'var(--accent)' }}>{hide ? '•••••' : fmt(dsTotal)}</strong></span> : null
          })()}
          {!isAdminView && currentUser?.role !== 'creator' && <span>My commissions: <strong style={{ color: '#8b5cf6' }}>{hide ? '•••••' : fmt(myTotalComm)}</strong></span>}
        </div>
      )}

      {/* Pagination controls — only relevant for table view with a page size cap */}
      {!hide && dealView === 'table' && filtered.length > 0 && (
        <div style={{
          marginTop: 14,
          paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text3)' }}>
            <span>Per page</span>
            <select
              value={pageSize}
              onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              style={{
                padding: '5px 8px', fontSize: 12, borderRadius: 6,
                border: '1px solid var(--border)', background: 'var(--bg2)',
                color: 'var(--text)', fontFamily: 'var(--font-body)', cursor: 'pointer',
              }}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value="all">All</option>
            </select>
            {pageSize !== 'all' && (
              <span>
                Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
              </span>
            )}
          </div>

          {pageSize !== 'all' && totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                style={{
                  padding: isMobile ? '10px 16px' : '5px 10px',
                  fontSize: isMobile ? 13 : 12, fontWeight: 600,
                  minHeight: isMobile ? 44 : 'auto', minWidth: isMobile ? 80 : 'auto',
                  background: 'transparent', color: safePage <= 1 ? 'var(--text3)' : 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  cursor: safePage <= 1 ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)',
                }}
              >← Prev</button>
              <span style={{ fontSize: 12, color: 'var(--text2)', padding: '0 6px' }}>
                Page <strong>{safePage}</strong> of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                style={{
                  padding: isMobile ? '10px 16px' : '5px 10px',
                  fontSize: isMobile ? 13 : 12, fontWeight: 600,
                  minHeight: isMobile ? 44 : 'auto', minWidth: isMobile ? 80 : 'auto',
                  background: 'transparent', color: safePage >= totalPages ? 'var(--text3)' : 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  cursor: safePage >= totalPages ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)',
                }}
              >Next →</button>
            </div>
          )}
        </div>
      )}

    </div>

    {/* Bulk-action floating bar — appears when 1+ deals are selected (admin/sales only) */}
    {(isAdmin || isSales) && selectedIds.size > 0 && (
      <div style={{
        position: 'fixed',
        bottom: `calc(20px + env(safe-area-inset-bottom))`,
        left: '50%', transform: 'translateX(-50%)',
        zIndex: 50,
        display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14,
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 12, padding: isMobile ? '12px 14px' : '10px 16px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
        minWidth: isMobile ? 'calc(100vw - 24px)' : 320,
        maxWidth: 'calc(100vw - 24px)',
        flexWrap: 'wrap', justifyContent: 'center',
      }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{selectedIds.size} selected</span>
        <span style={{ color: 'var(--text3)', fontSize: 12 }}>Set status →</span>
        <select
          defaultValue=""
          disabled={bulkUpdating}
          onChange={e => {
            const v = e.target.value
            if (!v) return
            handleBulkStatusChange(v)
            e.target.value = ''
          }}
          style={{
            padding: isMobile ? '10px 12px' : '6px 10px',
            fontSize: isMobile ? 14 : 13,
            minHeight: isMobile ? 44 : 'auto',
            borderRadius: 8,
            border: '1px solid var(--border)', background: 'var(--bg3)',
            color: 'var(--text)', fontFamily: 'var(--font-body)', cursor: 'pointer',
          }}
        >
          <option value="" disabled>Choose status…</option>
          {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {bulkUpdating && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Updating…</span>}
        <button
          onClick={() => setSelectedIds(new Set())}
          disabled={bulkUpdating}
          style={{
            padding: isMobile ? '10px 14px' : '6px 12px',
            fontSize: isMobile ? 13 : 12, fontWeight: 600,
            minHeight: isMobile ? 44 : 'auto',
            background: 'transparent', color: 'var(--text3)',
            border: '1px solid var(--border)', borderRadius: 8,
            cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}
        >Clear</button>
      </div>
    )}

    {modal && !hide && (
      <DealModal
        deal={modal === 'new' ? null : modal}
        prefill={modal === 'new' && convertLead ? leadToDealPrefill(convertLead, currentUser) : null}
        currentUser={currentUser}
        onClose={() => { setModal(null); releaseConvertLead() }}
        onSaved={() => { setModal(null); releaseConvertLead(); onRefresh() }}
        isMobile={isMobile} deals={deals} team={team} />
    )}
    </>
  )
}
